"use client";

import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import citiesData from "@/data/cities.json";
import { createCitySlug, normalizeCityName } from "@/utils/geo/cityHelpers";
import { createApiClient } from "@/utils/supabase/api";
import { createClient } from "@/utils/supabase/client";
import { User } from "@supabase/supabase-js";
import {
  LogOut,
  Menu,
  MoonIcon,
  PlusIcon,
  Settings,
  UserIcon,
  X,
  XIcon,
} from "lucide-react";

import { Database } from "@/types/supabase";
import { siteConfig } from "@/config/site";
import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { EventSheet } from "@/components/events/EventSheet";
import NotificationsDropdown from "@/components/misc/NotificationsDropdown";
import { UserAvatar } from "@/components/shared/UserAvatar";

import { CitiesSelect } from "./CitiesSelect";

// Add this interface to extend the User type
interface ExtendedUser extends User {
  slug?: string;
}

interface NavbarProps {
  user: ExtendedUser | null;
  citySlug: string;
  geoData: {
    city: string;
    country: string;
    latitude: string;
    longitude: string;
  };
}

interface City {
  id: number;
  city: string;
  latitude: number;
  longitude: number;
}

interface RouteProps {
  label: string;
  href: string;
}

const routeList: RouteProps[] = [
  { label: "Blog", href: "/blog" },
  { label: "Festivals", href: "/festivals" },
  { label: "Artists", href: "/artists" },
];

// Add this interface for the user profile data
interface UserProfileData {
  username: string;
  avatar_url: string | null;
  slug: string | null;
}

// Memoized navigation links component
const NavigationLinks = React.memo(function NavigationLinks() {
  return (
    <ul className="flex items-center space-x-6">
      {routeList.map((route: RouteProps) => (
        <li key={route.label}>
          <Link
            href={route.href}
            className="text-primary hover:text-primary font-mono text-xs font-light tracking-widest uppercase underline-offset-4 transition-colors hover:underline"
          >
            {route.label}
          </Link>
        </li>
      ))}
    </ul>
  );
});

// Memoized user menu component
const UserMenu = React.memo(function UserMenu({
  user,
  profile,
  avatarUrl,
  isAvatarLoading,
  isProfileLoading,
  onSignOut,
}: {
  user: ExtendedUser;
  profile: any;
  avatarUrl: string | null;
  isAvatarLoading: boolean;
  isProfileLoading: boolean;
  onSignOut: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <UserAvatar
          user={{
            id: user.id,
            avatar_url: avatarUrl,
            username: profile?.username || user.user_metadata.username,
            slug: profile?.slug || user.slug,
          }}
          size="md"
          linkToProfile={false}
          isLoading={isAvatarLoading || isProfileLoading}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="bg-background w-64 rounded-xl p-2 text-sm text-zinc-100"
        align="end"
      >
        <div className="border-b border-zinc-800 pb-2">
          <div className="flex items-center gap-2">
            <UserAvatar
              user={{
                id: user.id,
                avatar_url: avatarUrl,
                username: profile?.username || user.user_metadata.username,
                slug: profile?.slug || user.slug,
              }}
              size="md"
              linkToProfile={false}
              isLoading={isAvatarLoading || isProfileLoading}
            />
            <div>
              <div className="font-medium">
                {profile?.username || user.user_metadata.username}
              </div>
              <div className="text-xs text-zinc-400">{user.email}</div>
            </div>
          </div>
        </div>

        <div className="py-2">
          <DropdownMenuItem asChild>
            <Link href="/account">
              <Settings className="mr-2 h-4 w-4" />
              <span>Account settings</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link href="#">
              <UserIcon className="mr-2 h-4 w-4" />
              <span>Public Profile</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={onSignOut}
            className="flex w-full items-center hover:bg-zinc-800/50"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Log out</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

// Memoized mobile menu component
const MobileMenu = React.memo(function MobileMenu({
  isOpen,
  onClose,
  user,
  selectedCity,
  onCityChange,
  onClearCity,
  onSignOut,
  onAuth,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: ExtendedUser | null;
  selectedCity: string;
  onCityChange: (city: string, lat: number, lon: number) => void;
  onClearCity: () => void;
  onSignOut: () => void;
  onAuth: () => void;
}) {
  if (!isOpen) return null;

  return (
    <nav
      className="bg-background fixed inset-0 z-50 flex h-full w-full flex-col overflow-y-auto md:hidden"
      aria-label="Mobile navigation"
    >
      <div className="flex items-center justify-between p-4">
        <Link
          href="/"
          className="text-xl font-bold tracking-tight uppercase"
          onClick={onClose}
        >
          {siteConfig.name}
        </Link>
        <button
          onClick={onClose}
          className="text-primary hover:bg-accent hover:text-accent-foreground focus:ring-primary rounded-md p-2 focus:ring-1 focus:outline-hidden focus:ring-inset"
        >
          <span className="sr-only">Close menu</span>
          <X className="size-6" aria-hidden="true" strokeWidth="1" />
        </button>
      </div>
      <ul className="flex-1 space-y-4 px-4 pt-2 pb-6">
        <li className="flex items-center gap-1">
          <CitiesSelect
            onCityChange={onCityChange}
            selectedCity={selectedCity}
          />
          <Button size="icon" variant="ghost" onClick={onClearCity}>
            <XIcon className="size-4" />
          </Button>
        </li>
        {routeList.map((route: RouteProps) => (
          <li key={route.label}>
            <Link
              href={route.href}
              className="text-primary block rounded-md px-3 py-2 font-mono text-sm font-medium tracking-widest uppercase"
              onClick={onClose}
            >
              {route.label}
            </Link>
          </li>
        ))}
        <li>
          <Button
            onClick={() => {
              user ? onSignOut() : onAuth();
              onClose();
            }}
            variant="ghost"
            className="-ml-1 font-mono tracking-widest uppercase hover:bg-transparent"
          >
            {user ? "Sign Out" : "Sign In"}
          </Button>
        </li>
        {user && (
          <li>
            <EventSheet>
              <Button size="xs" variant="cream" onClick={onClose}>
                Create Event
                <PlusIcon className="ml-2 h-4 w-4" />
              </Button>
            </EventSheet>
          </li>
        )}
      </ul>
    </nav>
  );
});

export const Navbar = React.memo(function Navbar({
  user,
  citySlug,
  geoData,
}: NavbarProps) {
  const router = useRouter();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedCity, setSelectedCity] = useState<string>(geoData.city);
  const [selectedLat, setSelectedLat] = useState<number | null>(
    parseFloat(geoData.latitude) || null,
  );
  const [selectedLon, setSelectedLon] = useState<number | null>(
    parseFloat(geoData.longitude) || null,
  );
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isAvatarLoading, setIsAvatarLoading] = useState(true);
  const supabase = createClient();
  const { profile, isLoading: isProfileLoading } = useProfile(user?.id);

  // Memoize cities data transformation
  const cities = React.useMemo(
    () =>
      citiesData.map((city) => ({
        ...city,
        latitude: parseFloat(city.latitude),
        longitude: parseFloat(city.longitude),
      })),
    [],
  );

  useEffect(() => {
    if (geoData.city) {
      setSelectedCity(geoData.city);
    }
  }, [geoData.city]);

  useEffect(() => {
    if (cities.length > 0) {
      const cityFromParams = searchParams?.get("city") ?? "";
      if (cityFromParams) {
        const cityData = cities.find((c) =>
          c.city.toLowerCase().startsWith(cityFromParams.toLowerCase()),
        );
        if (cityData) {
          setSelectedCity(cityData.city);
          setSelectedLat(cityData.latitude);
          setSelectedLon(cityData.longitude);
        }
      } else {
        setSelectedCity(geoData.city);
        setSelectedLat(parseFloat(geoData.latitude) || null);
        setSelectedLon(parseFloat(geoData.longitude) || null);
      }
    }
  }, [searchParams, geoData, cities]);

  useEffect(() => {
    const fetchAvatar = async () => {
      if (user) {
        setIsAvatarLoading(true);
        try {
          const avatarUrl = profile?.avatar_url || null;
          setAvatarUrl(avatarUrl);
        } catch (error) {
          console.error("Error fetching avatar:", error);
          setAvatarUrl(null);
        } finally {
          setIsAvatarLoading(false);
        }
      } else {
        setIsAvatarLoading(false);
      }
    };

    fetchAvatar();
  }, [user, profile]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleAuth = useCallback(async () => {
    if (user) {
      return router.push("/account");
    }
    return router.push("/auth");
  }, [user, router]);

  const handleCityChange = useCallback(
    async (city: string, lat: number, lon: number) => {
      setSelectedCity(city);
      setSelectedLat(lat);
      setSelectedLon(lon);
      const citySlug = normalizeCityName(city);
      router.push(`/?city=${encodeURIComponent(citySlug)}`);
    },
    [router],
  );

  const handleClearCity = useCallback(async () => {
    setSelectedCity("");
    setSelectedLat(null);
    setSelectedLon(null);
    router.push("/");
    toast({
      title: "Location cleared",
      description: "Your location preferences have been reset.",
    });
    if (pathname === "/") {
      router.refresh();
    } else {
      router.push("/");
    }
  }, [router, toast, pathname]);

  const handleSignOut = useCallback(async () => {
    const apiClient = createApiClient(supabase);
    try {
      await apiClient.signOut();
      setAvatarUrl(null);
      router.refresh();
      router.push("/");
      toast({
        title: "Signed out successfully",
        description: "You have been logged out.",
      });
    } catch (error) {
      toast({
        title: "Error signing out",
        description: "An error occurred while signing out.",
        variant: "destructive",
      });
    }
  }, [supabase, router, toast]);

  return (
    <header className="relative z-50 w-full transition-all duration-300">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link
              href="/"
              className="font-sohne text-xl font-bold tracking-tight uppercase"
            >
              {siteConfig.name}
            </Link>
            <div className="hidden items-center gap-1 md:flex">
              <CitiesSelect
                onCityChange={handleCityChange}
                selectedCity={selectedCity}
              />
              <Button size="icon" variant="ghost" onClick={handleClearCity}>
                <XIcon className="size-4" />
              </Button>
            </div>

            <nav className="hidden md:block" aria-label="Main navigation">
              <NavigationLinks />
            </nav>
          </div>

          <div className="hidden items-center space-x-4 md:flex">
            {user ? (
              <>
                <NotificationsDropdown userId={user.id} />
                <EventSheet>
                  <Button size="xs" variant="cream" className="rounded-full">
                    Create Event
                    <PlusIcon className="ml-2 h-4 w-4" strokeWidth={1} />
                  </Button>
                </EventSheet>
                <UserMenu
                  user={user}
                  profile={profile}
                  avatarUrl={avatarUrl}
                  isAvatarLoading={isAvatarLoading}
                  isProfileLoading={isProfileLoading}
                  onSignOut={handleSignOut}
                />
              </>
            ) : (
              <Button
                onClick={handleAuth}
                variant="link"
                className="font-mono text-xs font-light tracking-widest uppercase"
              >
                Login
              </Button>
            )}
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-primary hover:bg-accent hover:text-accent-foreground focus:ring-primary inline-flex items-center justify-center rounded-md p-2 focus:ring-1 focus:outline-hidden focus:ring-inset md:hidden"
          >
            <span className="sr-only">Open main menu</span>
            {isOpen ? (
              <X className="size-6" aria-hidden="true" strokeWidth="1" />
            ) : (
              <Menu className="size-6" aria-hidden="true" strokeWidth="1" />
            )}
          </button>
        </div>
      </div>

      <MobileMenu
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        user={user}
        selectedCity={selectedCity}
        onCityChange={handleCityChange}
        onClearCity={handleClearCity}
        onSignOut={handleSignOut}
        onAuth={handleAuth}
      />
    </header>
  );
});
